/**
 * TcmEnablementStatusBadge — the enablement state as a Carbon tag.
 *
 * Badge text is the mockup appendix's "Five enablement state labels". Under
 * Carbon the states carry no hue (the Neutral Bad State Rule): not-enabled is
 * an outline tag, every in-flight and healthy state a default tag, and only
 * the failed verification leads with the Coral octagon, because it blocks
 * enablement until the script runs again.
 *
 * Diagnostic surface — no headline-verdict copy here. The panel title in
 * TcmEnablementPanel carries the one-sentence assessment per state.
 */

import { Tag } from '@/components/carbon/Tag'
import { IconSeverityCrit } from '@/components/carbon/icons'
import type { TagKind } from '@/components/carbon/types'
import type { TcmEnablementState } from '@/types/tcm'

interface TcmEnablementStatusBadgeProps {
  status: TcmEnablementState
  className?: string
}

// From the mockup appendix — do not paraphrase.
const STATE_LABEL: Record<TcmEnablementState, string> = {
  NotEnabled: 'Not Enabled',
  ScriptGenerated: 'Script Generated',
  VerificationPending: 'Verifying',
  Enabled: 'Enabled',
  ExoProvisioning: 'EXO Provisioning',
  Error: 'Error',
}

const STATE_KIND: Record<TcmEnablementState, TagKind> = {
  NotEnabled: 'outline',
  ScriptGenerated: 'default',
  VerificationPending: 'default',
  Enabled: 'default',
  ExoProvisioning: 'default',
  Error: 'default',
}

export default function TcmEnablementStatusBadge({ status, className }: TcmEnablementStatusBadgeProps) {
  return (
    <span role="status" aria-label={`Enablement state: ${STATE_LABEL[status]}`} className={className}>
      <Tag kind={STATE_KIND[status]} icon={status === 'Error' ? <IconSeverityCrit className="text-sev-crit" /> : undefined}>
        {STATE_LABEL[status]}
      </Tag>
    </span>
  )
}
